import { useEffect, useState } from "react";
import { Users, Loader2 } from "lucide-react";
import { CoordinatorLayout } from "@/components/layout/coordinator-layout";
import { SameTeamBadge, TeamCodeBadge } from "@/components/ui/same-team-badge";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface Member {
  membershipId: number;
  userId: string;
  role: string | null;
  jobRole: string | null;
  teamCode: string | null;
  membershipRole: string | null;
  joinedAt: string;
  displayName: string | null;
  email: string | null;
}

export default function CoordinatorTeam() {
  const [rows, setRows] = useState<Member[]>([]);
  const [viewerTeamCode, setViewerTeamCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      fetch(`${BASE}/api/coordinator/team`, { credentials: "include" }).then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)),
      ),
      fetch(`${BASE}/api/user/profile-summary`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null),
    ])
      .then(([d, me]) => {
        setRows(d as Member[]);
        setViewerTeamCode((me as { teamCode?: string | null } | null)?.teamCode ?? null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <CoordinatorLayout>
      <div className="p-8 max-w-6xl">
        <div className="flex items-center gap-3 mb-2">
          <Users className="h-6 w-6 text-sky-600" />
          <h1 className="text-2xl font-bold text-slate-900">Team roster</h1>
        </div>
        <p className="text-slate-600 mb-6">
          Active clinicians and staff in your practice. Used for routing queries
          and appointments.
        </p>

        {loading && (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
            {error}
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((m) => (
            <div
              key={m.membershipId}
              className="bg-white p-4 rounded-2xl border border-slate-200"
              data-testid={`team-member-${m.membershipId}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-slate-900">
                  {m.displayName ?? m.email ?? m.userId}
                </p>
                <SameTeamBadge viewerTeamCode={viewerTeamCode} otherTeamCode={m.teamCode} />
              </div>
              {m.email && <p className="text-xs text-slate-500">{m.email}</p>}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {m.role && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-sky-50 text-sky-700">
                    {m.role}
                  </span>
                )}
                {m.jobRole && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                    {m.jobRole}
                  </span>
                )}
                {m.membershipRole && m.membershipRole !== "member" && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                    {m.membershipRole}
                  </span>
                )}
                <TeamCodeBadge teamCode={m.teamCode} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </CoordinatorLayout>
  );
}
